import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';

type Sub = 'alertas_ai' | 'sugerencias' | 'stock' | 'movimientos';

type Alert = {
  id: string;
  sku: string;
  on_hand: number;
  velocity_30d: number;
  reorder_point: number;
  suggested_qty: number;
  lead_time_days: number;
  alert_level: 'ok' | 'warning' | 'urgent' | 'out';
  computed_at: string;
  product_id: string | null;
};

type InvRow = {
  id: string;
  product_id: string;
  product_sku: string;
  product_name: string;
  product_msrp_mxn: number | null;
  product_cost_mxn: number | null;
  on_hand: number;
  reserved: number;
  reorder_point: number;
  reorder_qty: number;
  lead_time_days: number;
  // Calculados
  available: number;
  sold_30d: number;
  sold_90d: number;
  in_quotes_live: number;       // qty en cotizaciones sent/viewed
  daily_velocity: number;        // unidades/día promedio
  days_of_stock: number | null;  // null = infinito (sin ventas)
  suggested_qty: number;
  suggestion_reason: string;
  urgency: 'critical' | 'warning' | 'ok';
};

type Movement = {
  id: string;
  inventory_id: string;
  kind: string;
  quantity: number;
  reason: string | null;
  reference: string | null;
  created_at: string;
  product_sku?: string;
  product_name?: string;
};

export function AdminInventario() {
  const [sub, setSub] = useState<Sub>('alertas_ai');
  const [showMovementForm, setShowMovementForm] = useState(false);
  const [, setReloadKey] = useState(0);

  return (
    <AdminContainer
      title="Inventario"
      eyebrow="Stock, alertas AI, movimientos y sugerencias"
      action={
        <button style={btnPrimary} onClick={() => setShowMovementForm(true)}>
          + Movimiento
        </button>
      }
    >
      {showMovementForm && (
        <MovementModal
          onClose={() => setShowMovementForm(false)}
          onSaved={() => { setShowMovementForm(false); setReloadKey(k => k + 1); }}
        />
      )}
      <div style={{ display: 'inline-flex', background: theme.bg, border: `1px solid ${theme.border}`, marginBottom: 20 }}>
        <SubBtn active={sub === 'alertas_ai'} onClick={() => setSub('alertas_ai')}>
          🤖 Alertas AI
        </SubBtn>
        <SubBtn active={sub === 'sugerencias'} onClick={() => setSub('sugerencias')}>
          ⚡ Sugerencias
        </SubBtn>
        <SubBtn active={sub === 'stock'} onClick={() => setSub('stock')}>
          📋 Stock por SKU
        </SubBtn>
        <SubBtn active={sub === 'movimientos'} onClick={() => setSub('movimientos')}>
          📜 Movimientos
        </SubBtn>
      </div>

      {sub === 'alertas_ai' && <AlertasAI />}
      {sub === 'sugerencias' && <Sugerencias />}
      {sub === 'stock' && <Stock />}
      {sub === 'movimientos' && <Movimientos />}
    </AdminContainer>
  );
}

// ===================== ALERTAS AI (cron diario) =====================

function AlertasAI() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState<'all' | 'urgent' | 'warning'>('all');

  const load = async () => {
    setLoading(true);
    let q = supabase.from('inventory_alerts')
      .select('id, sku, on_hand, velocity_30d, reorder_point, suggested_qty, lead_time_days, alert_level, computed_at, product_id')
      .is('resolved_at', null);
    if (filter === 'urgent') q = q.in('alert_level', ['urgent', 'out']);
    if (filter === 'warning') q = q.in('alert_level', ['warning', 'urgent', 'out']);
    q = q.order('alert_level', { ascending: false }).limit(200);
    const { data } = await q;
    setAlerts((data as any) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]);

  const refreshNow = async () => {
    setRefreshing(true);
    const { data: { session } } = await supabase.auth.getSession();
    await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/inventory-predict`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token ?? ''}` },
    });
    setRefreshing(false);
    load();
  };

  const counts = {
    out: alerts.filter(a => a.alert_level === 'out').length,
    urgent: alerts.filter(a => a.alert_level === 'urgent').length,
    warning: alerts.filter(a => a.alert_level === 'warning').length,
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setFilter('all')} style={subBtnStyle(filter === 'all')}>Todas</button>
          <button onClick={() => setFilter('urgent')} style={subBtnStyle(filter === 'urgent')}>
            🚨 Urgentes ({counts.out + counts.urgent})
          </button>
          <button onClick={() => setFilter('warning')} style={subBtnStyle(filter === 'warning')}>
            ⚠️ Atención ({counts.warning + counts.urgent + counts.out})
          </button>
        </div>
        <button onClick={refreshNow} disabled={refreshing} style={{
          background: theme.accent, color: theme.brand, border: 'none',
          padding: '8px 14px', fontSize: 11, fontWeight: 700,
          textTransform: 'uppercase', cursor: 'pointer', fontFamily: theme.font.sans,
          opacity: refreshing ? 0.5 : 1,
        }}>
          {refreshing ? 'Recalculando…' : '🔄 Recalcular ahora'}
        </button>
      </div>

      <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 12 }}>
        Cron diario 06:00 CDMX. Fórmula: reorder = velocity × lead_time × 1.2 · suggested = velocity × 60 días.
      </div>

      <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: theme.bgSoft }}>
              <Th>Nivel</Th>
              <Th>SKU</Th>
              <Th style={{ textAlign: 'right' }}>Stock</Th>
              <Th style={{ textAlign: 'right' }}>Velocity/día</Th>
              <Th style={{ textAlign: 'right' }}>Reorder pt</Th>
              <Th style={{ textAlign: 'right' }}>Lead time</Th>
              <Th style={{ textAlign: 'right' }}>Sugerido PO</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: theme.textMuted }}>Cargando…</td></tr>
            ) : alerts.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: theme.textMuted }}>
                Sin alertas. El cron diario aún no ha corrido o todo el stock está OK.
              </td></tr>
            ) : alerts.map(a => (
              <tr key={a.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                <Td><LevelTag level={a.alert_level} /></Td>
                <Td><span style={{ fontFamily: theme.font.mono, fontSize: 12 }}>{a.sku}</span></Td>
                <Td style={{ textAlign: 'right' }}>{Number(a.on_hand).toFixed(0)}</Td>
                <Td style={{ textAlign: 'right' }}>{Number(a.velocity_30d).toFixed(2)}</Td>
                <Td style={{ textAlign: 'right' }}>{Number(a.reorder_point).toFixed(0)}</Td>
                <Td style={{ textAlign: 'right' }}>{a.lead_time_days}d</Td>
                <Td style={{ textAlign: 'right', fontWeight: 700, color: theme.brand }}>
                  {Number(a.suggested_qty).toFixed(0)}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LevelTag({ level }: { level: string }) {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    out:     { bg: '#7F1D1D', color: 'white', label: '⛔ AGOTADO' },
    urgent:  { bg: '#DC2626', color: 'white', label: '🚨 URGENTE' },
    warning: { bg: '#F59E0B', color: 'white', label: '⚠️ ATENCIÓN' },
    ok:      { bg: '#10B981', color: 'white', label: '✓ OK' },
  };
  const s = map[level] ?? map.ok;
  return (
    <span style={{
      background: s.bg, color: s.color, padding: '3px 7px',
      fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
      textTransform: 'uppercase', borderRadius: 3,
    }}>{s.label}</span>
  );
}

const subBtnStyle = (active: boolean): React.CSSProperties => ({
  background: active ? theme.brand : theme.bg,
  color: active ? theme.textOnBrand : theme.brand,
  border: `1px solid ${active ? theme.brand : theme.border}`,
  padding: '8px 14px', fontSize: 11, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font.sans,
  textTransform: 'uppercase', letterSpacing: 0.4,
});

// ===================== SUGERENCIAS =====================

function Sugerencias() {
  const [rows, setRows] = useState<InvRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => {
    setLoading(true);

    // 1) Leer todo el inventario + producto
    const { data: invData } = await supabase
      .from('inventory')
      .select(`
        id, product_id, on_hand, reserved, reorder_point, reorder_qty, lead_time_days,
        product:products ( sku, name, msrp_mxn, status )
      `)
      .limit(500);
    const inv = (invData ?? []) as any[];

    // Costo de reposicion desde product_costs (privado, admin). Puede ser USD o MXN segun proveedor.
    const pcMap = new Map<string, number>();
    {
      const ids = inv.map((r: any) => r.product_id);
      const { data: pcs } = await supabase.from('product_costs')
        .select('product_id, supplier_cost').is('variant_id', null).in('product_id', ids);
      for (const c of (pcs as any[]) ?? []) if (c.supplier_cost != null) pcMap.set(c.product_id, Number(c.supplier_cost));
    }

    // 2) Ventas últimos 90 días (de orders confirmados)
    const ninetyDaysAgo = new Date(Date.now() - 90 * 86400 * 1000).toISOString();
    const thirtyDaysAgo = new Date(Date.now() - 30 * 86400 * 1000).toISOString();

    const { data: salesData } = await supabase
      .from('order_items')
      .select(`
        product_id, quantity,
        order:orders ( created_at, status )
      `)
      .gte('order.created_at', ninetyDaysAgo);

    const sold30 = new Map<string, number>();
    const sold90 = new Map<string, number>();
    (salesData ?? []).forEach((row: any) => {
      const o = row.order;
      if (!o || ['cancelled', 'returned'].includes(o.status)) return;
      const pid = row.product_id;
      const qty = Number(row.quantity);
      sold90.set(pid, (sold90.get(pid) ?? 0) + qty);
      if (o.created_at >= thirtyDaysAgo) {
        sold30.set(pid, (sold30.get(pid) ?? 0) + qty);
      }
    });

    // 3) Cotizaciones vivas (sent/viewed) — demanda probable
    const { data: quoteData } = await supabase
      .from('quote_items')
      .select(`
        product_id, quantity,
        quote:quotes ( status, kind )
      `);
    const inQuotes = new Map<string, number>();
    (quoteData ?? []).forEach((row: any) => {
      const q = row.quote;
      if (!q || !['sent', 'viewed'].includes(q.status)) return;
      // Solo cuenta cotizaciones a NULED (las to_client no afectan inventario directo)
      if (q.kind !== 'to_nuled') return;
      const pid = row.product_id;
      inQuotes.set(pid, (inQuotes.get(pid) ?? 0) + Number(row.quantity));
    });

    // 4) Calcular sugerencias
    const result: InvRow[] = inv
      .filter((r: any) => r.product?.status === 'active')
      .map((r: any) => {
        const sold30d = sold30.get(r.product_id) ?? 0;
        const sold90d = sold90.get(r.product_id) ?? 0;
        const inQuotesLive = inQuotes.get(r.product_id) ?? 0;
        const daily = sold90d / 90;
        const available = r.on_hand - r.reserved;
        // proyección: (available - cotizado) / velocidad diaria = días que aguanta
        const projected = available - inQuotesLive;
        const days = daily > 0 ? projected / daily : null;

        let urgency: 'critical' | 'warning' | 'ok' = 'ok';
        let reason = '';
        let suggested = 0;

        if (available <= 0 && (sold30d > 0 || inQuotesLive > 0)) {
          urgency = 'critical';
          reason = 'Sin stock con demanda activa';
          suggested = Math.max(r.reorder_qty, Math.ceil(daily * (r.lead_time_days * 2)), inQuotesLive + 10);
        } else if (available > 0 && days != null && days < r.lead_time_days) {
          urgency = 'critical';
          reason = `Aguantará ${Math.floor(days)}d, lead time es ${r.lead_time_days}d`;
          suggested = Math.max(r.reorder_qty, Math.ceil(daily * r.lead_time_days * 2));
        } else if (available <= r.reorder_point && r.reorder_point > 0) {
          urgency = 'warning';
          reason = `Bajó a punto de reorden (${r.reorder_point})`;
          suggested = r.reorder_qty || Math.ceil(daily * r.lead_time_days * 1.5);
        } else if (inQuotesLive > available && inQuotesLive > 0) {
          urgency = 'warning';
          reason = `Cotizaciones vivas (${inQuotesLive}) > disponible (${available})`;
          suggested = Math.max(r.reorder_qty, inQuotesLive - available + 10);
        }

        return {
          id: r.id,
          product_id: r.product_id,
          product_sku: r.product.sku,
          product_name: r.product.name,
          product_msrp_mxn: r.product.msrp_mxn ? Number(r.product.msrp_mxn) : null,
          product_cost_mxn: pcMap.get(r.product_id) ?? null,
          on_hand: r.on_hand,
          reserved: r.reserved,
          reorder_point: r.reorder_point,
          reorder_qty: r.reorder_qty,
          lead_time_days: r.lead_time_days,
          available,
          sold_30d: sold30d,
          sold_90d: sold90d,
          in_quotes_live: inQuotesLive,
          daily_velocity: daily,
          days_of_stock: days,
          suggested_qty: suggested,
          suggestion_reason: reason,
          urgency,
        };
      })
      .filter(r => r.urgency !== 'ok')
      .sort((a, b) => {
        if (a.urgency !== b.urgency) return a.urgency === 'critical' ? -1 : 1;
        return b.sold_30d - a.sold_30d;
      });

    setRows(result);
    setLoading(false);
  })(); }, []);

  const totalSuggestedCost = rows.reduce((s, r) =>
    s + (r.suggested_qty * (r.product_cost_mxn ?? 0)), 0);

  if (loading) return <div style={{ padding: 40, color: theme.textMuted, textAlign: 'center' }}>Calculando sugerencias…</div>;

  return (
    <>
      <div style={{
        background: theme.bgFooter,
        borderLeft: `4px solid ${theme.accent}`,
        padding: 16,
        marginBottom: 16,
        fontSize: 13,
        color: theme.textMuted,
        lineHeight: 1.6,
      }}>
        Sugerencias calculadas con: <strong>velocidad de venta últimos 90 días</strong> ·
        cotizaciones vivas a NULED · stock disponible (on_hand − reservado) ·
        lead time de cada SKU. Marca <span style={{ color: theme.err, fontWeight: 700 }}>crítico</span>
        cuando aguantará menos que el lead time, <span style={{ color: theme.warn, fontWeight: 700 }}>advertencia</span>
        cuando llegó al punto de reorden o cotizaciones &gt; stock.
      </div>

      {rows.length === 0 ? (
        <div style={{
          padding: 40, background: theme.bg, border: `1px dashed ${theme.border}`,
          textAlign: 'center', color: theme.textMuted,
        }}>
          ✓ Sin sugerencias urgentes. El inventario está sano.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 12 }}>
            <span style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 600, color: theme.brand }}>
              {rows.length} SKUs requieren atención
            </span>
            {totalSuggestedCost > 0 && (
              <span style={{ color: theme.textMuted, fontSize: 13 }}>
                · costo estimado de reposición: <strong style={{ color: theme.brand }}>
                  ${totalSuggestedCost.toLocaleString('es-MX', { maximumFractionDigits: 0 })} MXN
                </strong>
              </span>
            )}
          </div>

          <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1100 }}>
              <thead>
                <tr style={{ background: theme.bgSoft }}>
                  <Th>Urgencia</Th>
                  <Th>SKU</Th>
                  <Th>Producto</Th>
                  <Th style={{ textAlign: 'right' }}>Stock</Th>
                  <Th style={{ textAlign: 'right' }}>Cotizado</Th>
                  <Th style={{ textAlign: 'right' }}>Vendido 30d</Th>
                  <Th style={{ textAlign: 'right' }}>Vel/día</Th>
                  <Th style={{ textAlign: 'right' }}>Aguanta</Th>
                  <Th style={{ textAlign: 'right' }}>Lead time</Th>
                  <Th>Razón</Th>
                  <Th style={{ textAlign: 'right' }}>Sugerido</Th>
                  <Th style={{ textAlign: 'right' }}>Costo</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} style={{
                    borderBottom: `1px solid ${theme.border}`,
                    borderLeft: `3px solid ${r.urgency === 'critical' ? theme.err : theme.warn}`,
                  }}>
                    <Td>
                      <Tag color={r.urgency === 'critical' ? theme.err : theme.warn}>
                        {r.urgency === 'critical' ? '🔴 Crítico' : '🟡 Aviso'}
                      </Tag>
                    </Td>
                    <Td><span style={{ fontFamily: theme.font.mono, fontSize: 10, color: theme.textMuted }}>{r.product_sku}</span></Td>
                    <Td style={{ maxWidth: 220 }}>{r.product_name}</Td>
                    <Td style={{ textAlign: 'right' }}>
                      <strong>{r.available}</strong>
                      {r.reserved > 0 && (
                        <span style={{ color: theme.textMuted, marginLeft: 4 }}>
                          (de {r.on_hand}, {r.reserved} res.)
                        </span>
                      )}
                    </Td>
                    <Td style={{ textAlign: 'right', color: r.in_quotes_live > 0 ? theme.warn : theme.textMuted }}>
                      {r.in_quotes_live}
                    </Td>
                    <Td style={{ textAlign: 'right' }}>{r.sold_30d}</Td>
                    <Td style={{ textAlign: 'right' }}>{r.daily_velocity.toFixed(1)}</Td>
                    <Td style={{ textAlign: 'right' }}>
                      {r.days_of_stock != null ? `${r.days_of_stock.toFixed(0)} días` : '∞'}
                    </Td>
                    <Td style={{ textAlign: 'right', color: theme.textMuted }}>{r.lead_time_days}d</Td>
                    <Td style={{ fontSize: 11, color: theme.textMuted, maxWidth: 200 }}>{r.suggestion_reason}</Td>
                    <Td style={{ textAlign: 'right' }}>
                      <strong style={{ color: theme.brand, fontFamily: theme.font.head, fontSize: 14 }}>
                        {r.suggested_qty}
                      </strong>
                    </Td>
                    <Td style={{ textAlign: 'right', color: theme.textMuted }}>
                      {r.product_cost_mxn != null && r.product_cost_mxn > 0
                        ? `$${(r.suggested_qty * r.product_cost_mxn).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`
                        : '—'}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 16, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button style={{ ...btnPrimary, background: theme.brand, color: theme.textOnBrand }}
              onClick={() => alert('Generar PO masiva con todos los críticos · próximo commit')}>
              Generar PO con todos los críticos →
            </button>
            <button style={btnPrimary}
              onClick={() => alert('Exportar a CSV · próximo commit')}>
              Exportar CSV
            </button>
          </div>
        </>
      )}
    </>
  );
}

// ===================== STOCK POR SKU =====================

function Stock() {
  const [rows, setRows] = useState<any[]>([]);
  const [whs, setWhs] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [loc, setLoc] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('warehouses').select('code, name').eq('active', true).order('sort_order')
      .then(({ data }) => setWhs(data ?? []));
    supabase.from('inventory')
      .select(`
        id, on_hand, reserved, reorder_point, reorder_qty, lead_time_days, location,
        product:products ( sku, name, status, brand_code )
      `)
      .limit(2000)
      .then(async ({ data }) => {
        // Fases: Asignado / En tránsito / Entrante por fila de inventario
        const { data: ph } = await supabase.from('inventory_phases').select('inventory_id, sku, asignado, en_transito, entrante').limit(5000);
        const m = new Map<string, any>(((ph as any[]) ?? []).map(p => [p.inventory_id, p]));
        setRows(((data as any[]) ?? []).map(r => ({ ...r, ph: m.get(r.id) ?? null })));
        setLoading(false);
      });
  }, []);

  const whName = (code: string) => whs.find(w => w.code === code)?.name ?? code;

  const totals = new Map<string, number>();
  rows.forEach(r => {
    if (!r.product || r.product.status !== 'active') return;
    totals.set(r.location, (totals.get(r.location) ?? 0) + (r.on_hand - r.reserved));
  });

  const filtered = rows.filter(r => {
    if (!r.product) return false;
    if (r.product.status !== 'active') return false;
    if (loc !== 'all' && r.location !== loc) return false;
    if (!q.trim()) return true;
    const qn = q.trim().toLowerCase();
    return r.product.sku.toLowerCase().includes(qn) || String(r.ph?.sku ?? '').toLowerCase().includes(qn) || r.product.name.toLowerCase().includes(qn);
  });

  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        {whs.map(w => (
          <div key={w.code} style={{ background: theme.bg, border: `1px solid ${theme.border}`, padding: '10px 16px', minWidth: 160 }}>
            <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>{w.name}</div>
            <div style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 600, color: theme.brand }}>
              {(totals.get(w.code) ?? 0).toLocaleString('es-MX')}
            </div>
            <div style={{ fontSize: 10, color: theme.textMuted }}>unidades disponibles</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input
          placeholder="Buscar SKU o nombre…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{
            flex: 1, padding: '10px 14px', border: `1px solid ${theme.border}`, fontSize: 14,
            outline: 'none', fontFamily: theme.font.sans, color: theme.text, background: theme.bg, boxSizing: 'border-box',
          }}
        />
        <select value={loc} onChange={e => setLoc(e.target.value)} style={{
          padding: '10px 14px', border: `1px solid ${theme.border}`, fontSize: 13,
          background: theme.bg, color: theme.text, fontFamily: theme.font.sans, outline: 'none',
        }}>
          <option value="all">Todas las bodegas</option>
          {whs.map(w => <option key={w.code} value={w.code}>{w.name}</option>)}
        </select>
      </div>

      {loading ? <div style={{ padding: 20, color: theme.textMuted }}>Cargando…</div> : (
        <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 800 }}>
            <thead>
              <tr style={{ background: theme.bgSoft }}>
                <Th>SKU</Th>
                <Th>Producto</Th>
                <Th>Marca</Th>
                <Th>Bodega</Th>
                <Th style={{ textAlign: 'right' }}>Entrante</Th>
                <Th style={{ textAlign: 'right' }}>Físico</Th>
                <Th style={{ textAlign: 'right' }}>Disponible</Th>
                <Th style={{ textAlign: 'right' }}>Asignado</Th>
                <Th style={{ textAlign: 'right' }}>En tránsito</Th>
                <Th style={{ textAlign: 'right' }}>Reorder pt</Th>
                <Th style={{ textAlign: 'right' }}>Reorder qty</Th>
                <Th style={{ textAlign: 'right' }}>Lead time</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 300).map(r => {
                const available = r.on_hand - r.reserved;
                const stockColor = available === 0 ? theme.err : available <= r.reorder_point ? theme.warn : theme.accentDark;
                return (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <Td><span style={{ fontFamily: theme.font.mono, fontSize: 10, color: theme.textMuted }}>{r.ph?.sku ?? r.product.sku}</span></Td>
                    <Td>{r.product.name}</Td>
                    <Td>{r.product.brand_code}</Td>
                    <Td style={{ color: theme.textMuted, fontSize: 11 }}>{whName(r.location)}</Td>
                    <Td style={{ textAlign: 'right', color: '#6B21A8' }}>{Number(r.ph?.entrante ?? 0) || '—'}</Td>
                    <Td style={{ textAlign: 'right' }}>{r.on_hand}</Td>
                    <Td style={{ textAlign: 'right', color: stockColor, fontWeight: 700 }}>● {available}</Td>
                    <Td style={{ textAlign: 'right', color: '#3730A3' }}>{Number(r.ph?.asignado ?? 0) || '—'}</Td>
                    <Td style={{ textAlign: 'right', color: '#1D4ED8' }}>{Number(r.ph?.en_transito ?? 0) || '—'}</Td>
                    <Td style={{ textAlign: 'right' }}>{r.reorder_point}</Td>
                    <Td style={{ textAlign: 'right' }}>{r.reorder_qty}</Td>
                    <Td style={{ textAlign: 'right', color: theme.textMuted }}>{r.lead_time_days}d</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ===================== MOVIMIENTOS =====================

function Movimientos() {
  const [rows, setRows] = useState<Movement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('inventory_movements')
      .select(`
        id, kind, quantity, reason, reference, created_at,
        inventory:inventory ( product:products ( sku, name ) )
      `)
      .order('created_at', { ascending: false })
      .limit(100)
      .then(({ data }) => {
        const mapped = (data ?? []).map((r: any) => ({
          id: r.id,
          inventory_id: r.inventory_id,
          kind: r.kind,
          quantity: r.quantity,
          reason: r.reason,
          reference: r.reference,
          created_at: r.created_at,
          product_sku: r.inventory?.product?.sku,
          product_name: r.inventory?.product?.name,
        }));
        setRows(mapped);
        setLoading(false);
      });
  }, []);

  if (loading) return <div style={{ padding: 20, color: theme.textMuted }}>Cargando…</div>;

  if (rows.length === 0) return (
    <div style={{ padding: 40, background: theme.bg, border: `1px dashed ${theme.border}`, textAlign: 'center', color: theme.textMuted }}>
      Sin movimientos registrados aún. Aparecerán automáticamente al recibir compras o confirmar pedidos.
    </div>
  );

  return (
    <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: theme.bgSoft }}>
            <Th>Fecha</Th>
            <Th>Tipo</Th>
            <Th>SKU</Th>
            <Th>Producto</Th>
            <Th style={{ textAlign: 'right' }}>Cantidad</Th>
            <Th>Razón</Th>
            <Th>Referencia</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
              <Td style={{ color: theme.textMuted }}>{new Date(r.created_at).toLocaleString('es-MX')}</Td>
              <Td><Tag color={r.quantity > 0 ? theme.accentDark : theme.err}>{r.kind}</Tag></Td>
              <Td><span style={{ fontFamily: theme.font.mono, fontSize: 10, color: theme.textMuted }}>{r.product_sku}</span></Td>
              <Td>{r.product_name}</Td>
              <Td style={{ textAlign: 'right', fontWeight: 700, color: r.quantity > 0 ? theme.accentDark : theme.err }}>
                {r.quantity > 0 ? '+' : ''}{r.quantity}
              </Td>
              <Td>{r.reason ?? '—'}</Td>
              <Td style={{ fontFamily: theme.font.mono, fontSize: 10, color: theme.textMuted }}>{r.reference ?? '—'}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ===================== shared =====================

function SubBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: active ? theme.brand : 'transparent',
      color: active ? theme.textOnBrand : theme.brand,
      border: 'none',
      padding: '12px 20px',
      fontSize: 13,
      fontWeight: 700,
      cursor: 'pointer',
      fontFamily: theme.font.sans,
    }}>{children}</button>
  );
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th style={{
      textAlign: 'left',
      padding: '10px 12px',
      fontSize: 9,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: theme.textMuted,
      borderBottom: `1px solid ${theme.border}`,
      ...style,
    }}>{children}</th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '10px 12px', verticalAlign: 'top', ...style }}>{children}</td>;
}

function Tag({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 700,
      padding: '2px 8px',
      color,
      border: `1px solid ${color}`,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

const btnPrimary: React.CSSProperties = {
  background: theme.accent,
  color: theme.brand,
  border: 'none',
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: theme.font.sans,
};

// ============ MODAL: Movimiento manual ============
type SkuOpt = { product_id: string; variant_id: string | null; sku: string; name: string };

function MovementModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState<SkuOpt[]>([]);
  const [selected, setSelected] = useState<SkuOpt | null>(null);
  const [kind, setKind] = useState<'in' | 'out' | 'adjust' | 'count' | 'damage'>('in');
  const [quantity, setQuantity] = useState<number>(1);
  const [location, setLocation] = useState('oficina');
  const [warehouses, setWarehouses] = useState<{ code: string; name: string }[]>([]);
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    supabase.from('warehouses').select('code, name').eq('active', true).order('sort_order')
      .then(({ data }) => setWarehouses((data as any) ?? []));
  }, []);

  useEffect(() => {
    if (!q.trim() || q.length < 2) { setMatches([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      const { data } = await supabase.from('products')
        .select('id, sku, name')
        .or(`sku.ilike.%${q}%,name.ilike.%${q}%`)
        .limit(20);
      if (alive) {
        setMatches(((data as any[]) ?? []).map(r => ({
          product_id: r.id, variant_id: null, sku: r.sku, name: r.name,
        })));
      }
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!selected) { setErr('Selecciona un SKU primero'); return; }
    setSaving(true); setErr(null);
    try {
      const { error } = await supabase.rpc('register_inventory_movement', {
        p_product_id: selected.product_id,
        p_variant_id: selected.variant_id,
        p_location: location || 'oficina',
        p_kind: kind,
        p_quantity: quantity,
        p_reason: reason || null,
        p_reference: reference || null,
      });
      if (error) throw error;
      onSaved();
    } catch (e: any) {
      setErr(e.message ?? 'Error al registrar');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.bg, border: `1px solid ${theme.border}`,
        padding: 32, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto',
        position: 'relative',
      }}>
        <button type="button" onClick={onClose} aria-label="Cerrar" title="Cerrar" style={{ position: 'absolute', top: 10, right: 14, background: 'transparent', border: 'none', color: theme.textMuted, fontSize: 26, lineHeight: 1, cursor: 'pointer', padding: 4, zIndex: 5 }}>×</button>
        <div style={{ fontSize: 11, color: theme.accentDark, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>
          Movimiento de inventario
        </div>
        <h2 style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 400, color: theme.brand, margin: '4px 0 20px' }}>
          Registrar entrada / salida / ajuste
        </h2>

        <form onSubmit={submit}>
          <label style={{ display: 'block', marginBottom: 14 }}>
            <div style={mvLabel}>SKU o producto *</div>
            {selected ? (
              <div style={{
                padding: '10px 12px', border: `1px solid ${theme.brand}`, background: theme.bgFooter,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontSize: 11, fontFamily: theme.font.mono, color: theme.textMuted }}>{selected.sku}</div>
                  <div style={{ fontSize: 13, color: theme.brand }}>{selected.name}</div>
                </div>
                <button type="button" onClick={() => { setSelected(null); setQ(''); }} style={{
                  background: 'transparent', border: 'none', color: theme.textMuted, cursor: 'pointer', fontSize: 18,
                }}>×</button>
              </div>
            ) : (
              <>
                <input
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  placeholder="Busca SKU o nombre…"
                  style={mvInput}
                />
                {matches.length > 0 && (
                  <div style={{ border: `1px solid ${theme.border}`, marginTop: 4, maxHeight: 200, overflowY: 'auto', background: theme.bg }}>
                    {matches.map(m => (
                      <button
                        type="button"
                        key={m.product_id}
                        onClick={() => { setSelected(m); setQ(''); setMatches([]); }}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '8px 12px', border: 'none', borderBottom: `1px solid ${theme.border}`,
                          background: 'transparent', cursor: 'pointer', fontFamily: theme.font.sans,
                        }}
                      >
                        <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: theme.font.mono }}>{m.sku}</div>
                        <div style={{ fontSize: 13, color: theme.brand }}>{m.name}</div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ display: 'block', marginBottom: 14 }}>
              <div style={mvLabel}>Tipo *</div>
              <select value={kind} onChange={e => setKind(e.target.value as any)} style={mvInput}>
                <option value="in">📥 Entrada (compra/recepción)</option>
                <option value="out">📤 Salida (venta/uso)</option>
                <option value="adjust">⚖ Ajuste (delta ±)</option>
                <option value="count">📋 Conteo físico (valor final)</option>
                <option value="damage">⚠ Merma / daño</option>
              </select>
            </label>
            <label style={{ display: 'block', marginBottom: 14 }}>
              <div style={mvLabel}>Cantidad *</div>
              <input
                type="number"
                value={quantity}
                onChange={e => setQuantity(Number(e.target.value))}
                style={mvInput}
                required
                step="1"
              />
            </label>
          </div>

          <label style={{ display: 'block', marginBottom: 14 }}>
            <div style={mvLabel}>Bodega</div>
            <select value={location} onChange={e => setLocation(e.target.value)} style={mvInput}>
              {warehouses.map(w => <option key={w.code} value={w.code}>{w.name}</option>)}
            </select>
          </label>

          <label style={{ display: 'block', marginBottom: 14 }}>
            <div style={mvLabel}>Razón / Notas</div>
            <input value={reason} onChange={e => setReason(e.target.value)} style={mvInput} placeholder="Ej: Recepción contenedor abril" />
          </label>

          <label style={{ display: 'block', marginBottom: 14 }}>
            <div style={mvLabel}>Referencia (factura, PO, etc.)</div>
            <input value={reference} onChange={e => setReference(e.target.value)} style={mvInput} placeholder="PO-260511-001" />
          </label>

          {err && (
            <div style={{ color: '#B91C1C', fontSize: 13, padding: 10, background: 'rgba(220,38,38,0.05)', border: '1px solid #B91C1C' }}>
              {err}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{
              background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand,
              padding: '10px 18px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 0.4, cursor: 'pointer', fontFamily: theme.font.sans,
            }}>Cancelar</button>
            <button type="submit" disabled={saving || !selected} style={{
              ...btnPrimary, opacity: (saving || !selected) ? 0.6 : 1,
              cursor: (saving || !selected) ? 'not-allowed' : 'pointer',
            }}>{saving ? 'Guardando…' : 'Registrar movimiento'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const mvLabel: React.CSSProperties = {
  fontSize: 11, color: theme.textMuted, marginBottom: 6,
  textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
};
const mvInput: React.CSSProperties = {
  width: '100%', background: theme.bg, border: `1px solid ${theme.border}`,
  padding: '10px 12px', color: theme.text, fontSize: 13,
  fontFamily: theme.font.sans, outline: 'none', boxSizing: 'border-box',
};
